import { Transition } from "@headlessui/react"
import { Form, Head } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import OnboardingPasswordRules from "@/components/onboarding/onboarding-password-rules"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsPasswordPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

export interface SettingsPasswordProps {
  /** OKS-306 — Server payload shape; mirrors
   *  `User.password_requirements_payload`. Drives the rule-hint block
   *  rendered above the **new** password input only (the rules don't
   *  apply to the current-password challenge field). */
  password_requirements: {
    min_length: number
    rule_keys: string[]
  }
}

export default function Password({
  password_requirements,
}: SettingsPasswordProps) {
  const { t } = useTranslation()
  const passwordRulesId = "settings-password-rules"

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () => [
      {
        title: t("settings.passwords.breadcrumb"),
        href: settingsPasswordPath(),
      },
    ],
    [t],
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.passwords.title")}
            description={t("settings.passwords.description")}
          />

          <Form
            method="put"
            action={settingsPasswordPath()}
            options={{
              preserveScroll: true,
            }}
            resetOnError
            resetOnSuccess
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="password_challenge">
                    {t("settings.passwords.current_label")}
                  </Label>

                  <Input
                    id="password_challenge"
                    name="password_challenge"
                    type="password"
                    className="mt-1 block w-full"
                    autoComplete="current-password"
                    placeholder={t("settings.passwords.current_placeholder")}
                  />

                  <InputError messages={errors.password_challenge} />
                </div>

                <OnboardingPasswordRules
                  id={passwordRulesId}
                  minLength={password_requirements.min_length}
                  ruleKeys={password_requirements.rule_keys}
                />

                <div className="grid gap-2">
                  <Label htmlFor="password">
                    {t("settings.passwords.new_label")}
                  </Label>

                  <Input
                    id="password"
                    name="password"
                    type="password"
                    className="mt-1 block w-full"
                    autoComplete="new-password"
                    placeholder={t("settings.passwords.new_placeholder")}
                    aria-describedby={[
                      passwordRulesId,
                      errors.password ? "settings-password-error" : null,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />

                  <InputError
                    id="settings-password-error"
                    messages={errors.password}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="password_confirmation">
                    {t("settings.passwords.confirm_label")}
                  </Label>

                  <Input
                    id="password_confirmation"
                    name="password_confirmation"
                    type="password"
                    className="mt-1 block w-full"
                    autoComplete="new-password"
                    placeholder={t("settings.passwords.confirm_placeholder")}
                  />

                  <InputError messages={errors.password_confirmation} />
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>
                    {t("settings.passwords.save")}
                  </Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-neutral-600">
                      {t("settings.passwords.saved")}
                    </p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
